import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type * as React from "react";
import cn from "twsx";

// An "on this page" rail: a framed column of section buttons, each with a
// status icon. Groups with several sections nest their children under a
// hairline. Placement (width, margin, visibility) stays with the page.
/** Use when a long page needs an "on this page" list of its sections. */
export function SectionNav(t0) {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex flex-col overflow-y-auto overscroll-contain p-1.5", "rounded-settings-list-view-item border border-border-popover bg-bg-surface", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <nav ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function SectionNavGroup(t0) {
  const $ = _c(6);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  let t1;
  if ($[3] !== props || $[4] !== ref) {
    t1 = <div ref={ref} className="ml-4 flex flex-col border-l border-border-subtle py-0.5 pl-1.5" {...props} />;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
const sectionNavItemVariants = cva(["flex w-full min-w-0 cursor-pointer items-center text-left outline-none [&_svg]:shrink-0", "focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent"], {
  variants: {
    level: {
      group: "h-8 gap-2 rounded-menu-item px-2 text-sm [&_svg]:size-3.5",
      child: "h-7 gap-1.5 rounded-r-menu-item px-1.5 text-xs [&_svg]:size-3"
    },
    // `selected` is the section in view; `open` is the group that holds it
    // while its children are expanded.
    state: {
      default: "text-text-tertiary hover:bg-fill-hover-subtle hover:text-text-secondary",
      open: "text-text-primary",
      selected: "bg-fill-hover-subtle text-text-primary"
    },
    done: {
      true: "",
      false: ""
    },
    hasStatusLabel: {
      true: "h-auto min-h-8 py-1",
      false: ""
    }
  },
  compoundVariants: [{
    level: "group",
    state: "selected",
    className: "font-medium"
  }, {
    state: "default",
    done: true,
    className: "opacity-60"
  }],
  defaultVariants: {
    level: "group",
    state: "default",
    done: false
  }
});
const iconTones = {
  default: "",
  success: "[&>svg:first-child]:text-green-text",
  warning: "[&>svg:first-child]:text-yellow-text",
  danger: "[&>svg:first-child]:text-red-text"
} as const;
export function SectionNavItem(t0) {
  const $ = _c(32);
  let done;
  let icon;
  let label;
  let level;
  let props;
  let ref;
  let state;
  let statusLabel;
  let t1;
  let trailing;
  if ($[0] !== t0) {
    ({
      level,
      state,
      done,
      iconTone: t1,
      icon,
      label,
      statusLabel,
      trailing,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = done;
    $[2] = icon;
    $[3] = label;
    $[4] = level;
    $[5] = props;
    $[6] = ref;
    $[7] = state;
    $[8] = statusLabel;
    $[9] = t1;
    $[10] = trailing;
  } else {
    done = $[1];
    icon = $[2];
    label = $[3];
    level = $[4];
    props = $[5];
    ref = $[6];
    state = $[7];
    statusLabel = $[8];
    t1 = $[9];
    trailing = $[10];
  }
  const iconTone = t1 === undefined ? "default" : t1;
  let t2;
  if ($[11] !== done || $[12] !== iconTone || $[13] !== level || $[14] !== state || $[15] !== statusLabel) {
    t2 = cn(sectionNavItemVariants({
      level,
      state,
      done,
      hasStatusLabel: Boolean(statusLabel)
    }), iconTones[iconTone]);
    $[11] = done;
    $[12] = iconTone;
    $[13] = level;
    $[14] = state;
    $[15] = statusLabel;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const t3 = statusLabel ? `${label}: ${statusLabel}` : label;
  let t4;
  if ($[17] !== label) {
    t4 = <span className="block truncate">{label}</span>;
    $[17] = label;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== statusLabel) {
    t5 = statusLabel && <span data-section-nav-status={true} className="block text-xs text-text-secondary">{statusLabel}</span>;
    $[19] = statusLabel;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t3 || $[22] !== t4 || $[23] !== t5) {
    t6 = <span className="min-w-0 flex-1" title={t3}>{t4}{t5}</span>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== icon || $[26] !== props || $[27] !== ref || $[28] !== t2 || $[29] !== t6 || $[30] !== trailing) {
    t7 = <button ref={ref} type="button" className={t2} {...props}>{icon}{t6}{trailing}</button>;
    $[25] = icon;
    $[26] = props;
    $[27] = ref;
    $[28] = t2;
    $[29] = t6;
    $[30] = trailing;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  return t7;
}